import {
  Button,
  FormProvider,
  Link,
  Section,
  NotificationCard,
  RHFCodeInput,
} from "@arp/react-ndbx";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Fade, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import {
  AURA_VALIDATION_MESSAGES,
  TRACKIFY_EVENT,
  optVerificationModalContent,
  URLS
} from "../../../config/const/enums";
import { useVerifyOtpMutation } from "../../../hooks/dataHooks/useVerifyOtpMutation";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages";
import useSharedData from "../../../hooks/useSharedData";
import { formatMobileNumberMasked } from "../../../utilities/mappingHelper";
import { getAzureJWTToken } from "../../../utilities/auraHealthUtils";
import { useSendOtpMutation } from "../../../hooks/dataHooks/useSendOtpMutation";
import { trackifyPageChange } from "@allianz/tracking";
// import ReactCodeInput from "@acusti/react-code-input";

function OtpVerificationCodeModal({ userMobileNumber }) {
  const { state, funcs } = usePreQualificationPages();
  const mutation = useVerifyOtpMutation();
  const sendOtpMutation = useSendOtpMutation();

  const { accessToken } = useSharedData();
  // const accessToken = getAzureJWTToken();

  const [smsVerificationFailed, setSmsVerificationFailed] = useState(false);
  const [smsVerificationErrMsg, setSmsVerificationErrMsg] = useState("");
  const [disableResendButton, setDisableResendButton] = useState(false);
  const [successResendOtp, setSuccessResendOtp] = useState(false);
  const [resendSMSFailed, setResendSMSFailed] = useState(false);
  const [resendSMSFailedMessage, setResendSMSFailedMessage] = useState("");

  useEffect(() => {
    trackifyPageChange(TRACKIFY_EVENT.OTP_VALIDATION_MODAL);
  }, []);

  const schema = yup.object().shape({
    verificationCode: yup
      .string()
      .required(AURA_VALIDATION_MESSAGES.FIELD_REQUIRED)
      .length(6, ""),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "all",
  });

  const {
    control,
    formState: { errors, isValid },
    reset,
    getValues,
  } = methods;

  const validateOtp = async () => {
    try {
      methods.trigger().then(async (isValid) => {
        if (isValid) {
          const formData = getValues();
          if (formData?.verificationCode) {
            const payload = {
              accessToken: accessToken,
              verificationCode: formData.verificationCode,
            };
            const otpVerificationResponse = await mutation.mutateAsync(payload);
            if (otpVerificationResponse.status === 200) {
              await funcs.validateAndProceed();
            } else {
              setSmsVerificationFailed(true);
              setSuccessResendOtp(false);
              if (otpVerificationResponse?.response?.status === 403) {
                reset();
                setSmsVerificationErrMsg(
                  optVerificationModalContent.OTP_VERIFICATION_FAILED
                );
              } else {
                setSmsVerificationErrMsg(
                  optVerificationModalContent.GENERIC_CODE_VERIFICATION_ERROR
                );
              }
            }
          }
        }
      });
    } catch (error) {
      setSmsVerificationFailed(true);
      setSuccessResendOtp(false);
      setSmsVerificationErrMsg(
        optVerificationModalContent.GENERIC_CODE_VERIFICATION_ERROR
      );
    }
  };

  const resendOtp = async () => {
    try {
      reset();
      setDisableResendButton(true);
      const otpResendResponse = await sendOtpMutation.mutateAsync(accessToken);
      if (otpResendResponse?.status === 200) {
        setSuccessResendOtp(true);
        setSmsVerificationFailed(false);
        setResendSMSFailed(false);
      } else {
        setResendSMSFailed(true);
        if (otpResendResponse?.response?.status === 429) {
          setResendSMSFailedMessage(optVerificationModalContent.SMS_SEND_WAIT);
        } else {
          setResendSMSFailedMessage(
            optVerificationModalContent.GENERIC_SMS_ERROR
          );
        }
      }
    } catch (error) {
      setResendSMSFailed(true);
      setResendSMSFailedMessage(optVerificationModalContent.GENERIC_SMS_ERROR);
    }
    // enable the resend sms link after 30 sec as per Okta guidelines
    setTimeout(() => {
      setDisableResendButton(false);
    }, 30000);
  };

  return (
    <FormProvider methods={methods}>
      <Section
        controlIdentifier="otp-verification-code"
        sx={{
          pt: { xs: 5, sm: 8 },
          mt: 0,
          mb: 0,
        }}
        onKeyDownCapture={(e) => {
					if (isValid) validateOtp();
				}}
      >
        <Section
          sx={{
            mt: 0,
            mb: 5,
            pl: { xs: 3, sm: 8 },
            pr: { xs: 3, sm: 8 },
          }}
        >
          <Typography variant="h3-bold">
            {optVerificationModalContent.HELP_CONFIRM_IDENTITY}
          </Typography>
        </Section>
        <Section
          sx={{
            mt: 0,
            mb: 5,
            pl: { xs: 3, sm: 8 },
            pr: { xs: 3, sm: 8 },
          }}
        >
          <Typography variant="label-large-regular">
            {optVerificationModalContent.ENTER_CODE}{" "}
            {formatMobileNumberMasked(userMobileNumber)}
          </Typography>
        </Section>
        <Section
          mb={{ xs: 5, md: 8 }}
          sx={{
            pl: { xs: 1, sm: 8 },
            pr: { xs: 1, sm: 8 },
          }}
        >
          <RHFCodeInput
            name="verificationCode"
            className="verification-code"
            label="Verification Code"
            digitCodeNumber={6}
            // customError={smsVerificationFailed}
          />
        </Section>
        {"true" === import.meta.env.VITE_SKIP_SEND_SMS_MODAL && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="info"
                  title="Important information"
                  message={
                    <>
                      If the information you've provided is incorrect, call our
                      friendly Life Insurance Specialists on{" "}
                      <Link href={URLS.CONTACT_US_PHONE_URL}>
                        {URLS.CONTACT_US_PHONE_NUMBER}
                      </Link>{" "}
                      to discuss your application.
                    </>
                  }
                />
              </Box>
            </Fade>
          </Section>
        )}
        {smsVerificationFailed && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="error"
                  title=""
                  message={smsVerificationErrMsg}
                />
              </Box>
            </Fade>
          </Section>
        )}
        {successResendOtp && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="success"
                  title=""
                  message={optVerificationModalContent.RESEND_SMS_CODE}
                />
              </Box>
            </Fade>
          </Section>
        )}
        {resendSMSFailed && (
          <Section
            mb={4}
            sx={{
              px: { xs: 3, sm: 8 },
            }}
          >
            <Fade in={true} timeout={500}>
              <Box>
                <NotificationCard
                  cardType="error"
                  title=""
                  message={resendSMSFailedMessage}
                />
              </Box>
            </Fade>
          </Section>
        )}
        <Section mb={3}>
          <Section sx={{ maxWidth: { xs: "250px" } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={validateOtp}
              disabled={!isValid || mutation.isLoading}
            >
              NEXT
            </Button>
          </Section>
        </Section>
        <Section sx={{ mb: 2, mt: 0 }}>
          <Typography variant="p3">
            {optVerificationModalContent.DIDNT_RECIEVE_CODE}
          </Typography>
        </Section>
        <Section>
          <Typography variant="p3">
            <Link
              disabled={disableResendButton}
              onClick={resendOtp}
              component="button"
              type="button"
            >
              {optVerificationModalContent.RESEND_CODE_PHONE}
            </Link>
          </Typography>
        </Section>
      </Section>
    </FormProvider>
  );
}

export default OtpVerificationCodeModal;
